/**
 * Ryedical — Animations, Interactions & Scroll Behaviour
 * v4 — March 2026
 */
(function () {
  'use strict';

  /* ---- Respect reduced-motion preference ---- */
  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- Shared anchor-scroll helper (80px offset clears the sticky bar) ---- */
  function scrollToAnchor(id, offset) {
    if (offset == null) offset = 80;
    var el = document.getElementById(id);
    if (!el) return;
    var top = el.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({ top: top, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
  }

  /* ==========================================================================
     Scroll Reveal — data-animate system (v4)
     ========================================================================== */
  function initScrollReveal() {
    var elements = document.querySelectorAll('[data-animate]');
    if (!elements.length) return;

    if (prefersReducedMotion) {
      elements.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });

    elements.forEach(function (el) { observer.observe(el); });

    // Stagger children with data-stagger on parent
    document.querySelectorAll('[data-stagger]').forEach(function (parent) {
      var delay = parseInt(parent.dataset.stagger) || 80;
      parent.querySelectorAll('[data-animate]').forEach(function (child, i) {
        child.style.transitionDelay = (i * delay) + 'ms';
      });
    });
  }

  /* ==========================================================================
     Scroll Reveal — rye-reveal system (v3 backward compat)
     ========================================================================== */
  function initScrollRevealLegacy() {
    var reveals = document.querySelectorAll('.rye-reveal');
    if (!reveals.length) return;

    if (prefersReducedMotion) {
      reveals.forEach(function (el) { el.classList.add('rye-revealed'); });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var el = entry.target;
          var delay = parseFloat(el.getAttribute('data-rye-delay')) || 0;
          setTimeout(function () { el.classList.add('rye-revealed'); }, delay * 1000);
          observer.unobserve(el);
        }
      });
    }, { threshold: 0.15 });

    reveals.forEach(function (el) { observer.observe(el); });
  }

  /* ==========================================================================
     Smooth Scroll for Anchor Links
     ========================================================================== */
  function initSmoothScroll() {
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[href^="#"]');
      if (!link) return;
      var href = link.getAttribute('href');
      if (href === '#') return;
      /* Tolerate query-style suffixes like "#monthly-box?plan=subscribe" */
      var id = href.slice(1).split('?')[0];
      if (!id) return;
      var target = document.getElementById(id);
      if (!target) return;
      e.preventDefault();
      if (href.indexOf('plan=subscribe') !== -1) preselectSubscribe();
      scrollToAnchor(id, 80);
    });
  }

  /* Select the Subscribe & save radio on the buy card (shared by the footer
     deep link and the on-load fragment handler). */
  function preselectSubscribe() {
    var subOpt = document.querySelector('#ryedical-early-offer [data-eo-opt][data-selling-plan]:not([data-selling-plan=""])');
    if (!subOpt) return;
    var radio = subOpt.querySelector('[data-eo-radio]');
    if (radio && !radio.checked) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /* ==========================================================================
     Fragment pre-selection — footer "Subscribe & save" deep link
     (#monthly-box?plan=subscribe) pre-selects the subscribe radio on load.
     ========================================================================== */
  function initFragmentPreselect() {
    var hash = window.location.hash || '';
    if (hash.indexOf('plan=subscribe') === -1) return;
    preselectSubscribe();
    /* Let layout settle, then scroll the buy card into view. */
    setTimeout(function () { scrollToAnchor('monthly-box', 80); }, 120);
  }

  /* ==========================================================================
     Sticky Add-to-Cart Bar (v4)
     Uses #ryedical-product-card and #ryedical-sticky-atc
     ========================================================================== */
  function initStickyATC() {
    var stickyBar = document.getElementById('ryedical-sticky-atc');
    if (!stickyBar) return;

    var mode = stickyBar.getAttribute('data-mode');

    /* State machine — two independent inputs decide visibility:
         scrollWantsVisible: set by IntersectionObservers (scroll position)
         cartWantsHidden:    set by initStickyCart() when cart has items
       Apply combined state every change. Mutually-exclusive with sticky-cart.
    */
    var scrollWantsVisible = false;
    var cartWantsHidden    = false;

    function applyVisibility() {
      var visible = scrollWantsVisible && !cartWantsHidden;
      if (visible) {
        stickyBar.classList.add('is-visible');
        stickyBar.setAttribute('aria-hidden', 'false');
      } else {
        stickyBar.classList.remove('is-visible');
        stickyBar.setAttribute('aria-hidden', 'true');
      }
    }

    function showBar() { scrollWantsVisible = true;  applyVisibility(); }
    function hideBar() { scrollWantsVisible = false; applyVisibility(); }

    /* Expose for sticky-cart to flip the cart-side flag. */
    stickyBar.__setCartWantsHidden = function (val) {
      cartWantsHidden = !!val;
      applyVisibility();
    };

    if (mode === 'pdp') {
      /* ============== PDP MODE ==============
         Single observer on the main Add to Cart button area.
         Show when it scrolls out of view above the viewport. */
      var pdpTarget = document.querySelector('.product-form__buttons');
      if (!pdpTarget) return;

      var pdpObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            hideBar();
          } else if (entry.boundingClientRect.top < 0) {
            showBar();
          }
        });
      }, { threshold: 0 });
      pdpObserver.observe(pdpTarget);

    } else {
      /* ============== HOMEPAGE MODE ==============
         TWO observers — fixes the bug where the bar only appeared AFTER
         the visitor passed the product card.

         Observer 1 (HERO): show the bar when hero exits the viewport above.
         Observer 2 (PRODUCT CARD): hide the bar when product card enters view.

         Result: bar is visible across the entire mid-funnel (~7%–66% scroll)
         exactly where it's needed. Hides naturally when product card is on screen.
         After product card scrolls past, bar stays hidden (other CTAs handle
         the post-product-card sections — FAQ CTA, final CTA). */
      /* The trailing ryedical-re-* lookups are the Rye Effect landing page,
         which namespaces its sections to keep them off the shared ones. They
         are LAST in each chain and only evaluate when the earlier ids are
         absent from the document, so no existing page reaches them: the
         homepage still resolves #ryedical-hero and #ryedical-early-offer
         first, and the two id sets never coexist in one document. */
      var heroSection = document.getElementById('ryedical-hero')
                     || document.getElementById('ryedical-re-hero');
      /* Prefer the real early-offer section over the zero-height #ryedical-product-card
         anchor — IntersectionObserver on a 1px-tall <a> only fires for a single frame,
         which previously caused the bar to hide forever once you scrolled past. */
      var productCard = document.getElementById('ryedical-early-offer')
                     || document.getElementById('ryedical-product-card')
                     || document.getElementById('ryedical-re-offer');

      /* Track each input independently so the bar correctly reappears
         after the early-offer scrolls past below. */
      var heroInView = true;          /* assume true until proven otherwise */
      var cardInView = false;

      function recompute() {
        if (heroInView)      { hideBar(); }
        else if (cardInView) { hideBar(); }
        else                 { showBar(); }
      }

      if (heroSection) {
        var heroObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            heroInView = entry.isIntersecting;
            recompute();
          });
        }, { threshold: 0 });
        heroObserver.observe(heroSection);
      } else {
        heroInView = false;
      }

      if (productCard) {
        var cardObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            cardInView = entry.isIntersecting;
            recompute();
          });
        }, { threshold: 0 });
        cardObserver.observe(productCard);
      }

      /* Homepage CTA button (BUY →) is an on-page anchor to #monthly-box.
         The global initSmoothScroll handler intercepts it with the shared
         80px offset — no per-button handler needed here. */
    }

    /* PDP mode — sticky button triggers the MAIN product form's submit.
       This inherits all the Taste theme's AJAX cart-drawer behaviour
       (cart drawer slide-in, cart icon update, current variant selection). */
    if (mode === 'pdp') {
      var triggerBtn = stickyBar.querySelector('.ryedical-sticky-atc__btn--trigger');
      if (triggerBtn) {
        triggerBtn.addEventListener('click', function (e) {
          e.preventDefault();
          if (triggerBtn.disabled) return;

          /* Find the main product form on the page */
          var mainForm = document.querySelector('product-form form');
          if (!mainForm) {
            console.error('Ryedical sticky ATC: main product form not found');
            return;
          }

          /* Briefly show "Adding..." feedback */
          var originalText = triggerBtn.textContent;
          triggerBtn.disabled = true;
          triggerBtn.textContent = 'Adding...';

          /* Submit the main form — product-form.js intercepts and runs the AJAX flow */
          if (typeof mainForm.requestSubmit === 'function') {
            mainForm.requestSubmit();
          } else {
            /* Fallback for older browsers */
            var submitBtn = mainForm.querySelector('[type="submit"]');
            if (submitBtn) submitBtn.click();
          }

          /* Restore button state after a short delay (the cart drawer
             slide-in is the real visual confirmation) */
          setTimeout(function () {
            triggerBtn.disabled = false;
            triggerBtn.textContent = originalText;
          }, 1200);
        });
      }
    }
  }

  /* ==========================================================================
     FAQ Accordion (v4 classes: .ryedical-faq__question / .ryedical-faq__item)
     ========================================================================== */
  function initFAQ() {
    var questions = document.querySelectorAll('.ryedical-faq__question');
    if (!questions.length) return;

    questions.forEach(function (question, index) {
      question.addEventListener('click', function () {
        var item = this.closest('.ryedical-faq__item');
        var answer = item.querySelector('.ryedical-faq__answer');
        var isOpen = item.classList.contains('is-open');

        // Close all
        document.querySelectorAll('.ryedical-faq__item.is-open').forEach(function (openItem) {
          openItem.classList.remove('is-open');
          var a = openItem.querySelector('.ryedical-faq__answer');
          if (a) a.style.maxHeight = '0';
        });

        // Open clicked if it was closed
        if (!isOpen) {
          item.classList.add('is-open');
          answer.style.maxHeight = answer.scrollHeight + 'px';
        }
      });

      // Pre-open the top 3 Qs by default (audit §1.14)
      if (index < 3) {
        var item = question.closest('.ryedical-faq__item');
        var answer = item.querySelector('.ryedical-faq__answer');
        item.classList.add('is-open');
        question.setAttribute('aria-expanded', 'true');
        if (answer) {
          // Use setTimeout to allow layout to settle
          setTimeout(function () {
            answer.style.maxHeight = answer.scrollHeight + 'px';
          }, 50);
        }
      }
    });

    /* Audit (FAQ clipping fix) — recompute max-height for every open
       answer whenever fonts finish loading or the viewport resizes.
       Without this, the panels are measured before Lora is ready and
       the bottom of long answers gets clipped. */
    function recomputeOpenAnswers() {
      document.querySelectorAll('.ryedical-faq__item.is-open .ryedical-faq__answer').forEach(function (a) {
        a.style.maxHeight = a.scrollHeight + 'px';
      });
    }
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(recomputeOpenAnswers);
    }
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(recomputeOpenAnswers, 120);
    });
  }

  /* ==========================================================================
     Header Bottom Position (for mobile menu drawer)
     ========================================================================== */
  function initHeaderBottomPosition() {
    function setIt() {
      var hw = document.querySelector('.header-wrapper, sticky-header');
      if (hw) {
        var bottom = Math.round(hw.getBoundingClientRect().bottom);
        if (bottom > 0) {
          document.documentElement.style.setProperty('--header-bottom-position', bottom + 'px');
        }
      }
    }
    setIt();
    window.addEventListener('resize', setIt);
  }

  /* ==========================================================================
     Sticky Cart Bar — visible after first ATC. Mutually exclusive with sticky-ATC.
     Listens to PUB_SUB_EVENTS.cartUpdate and reads live cart state from /cart.js.
     Click on the bar opens the Taste theme cart-drawer (no page navigation).
     ========================================================================== */
  function initStickyCart() {
    var stickyCart = document.getElementById('ryedical-sticky-cart');
    if (!stickyCart) return;

    var stickyBar      = document.getElementById('ryedical-sticky-atc');
    var trigger        = stickyCart.querySelector('[data-sticky-cart-trigger]');
    var countEl        = stickyCart.querySelector('[data-sticky-cart-count]');
    var summaryEl      = stickyCart.querySelector('[data-sticky-cart-summary]');
    var totalEl        = stickyCart.querySelector('[data-sticky-cart-total]');

    function formatMoney(cents) {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        try { return window.Shopify.formatMoney(cents); } catch (e) {}
      }
      var amount = (cents / 100).toFixed(2);
      var sym = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active === 'USD') ? '$' : '$';
      return sym + amount;
    }

    function showCart() {
      stickyCart.hidden = false;
      stickyCart.getBoundingClientRect();
      stickyCart.classList.add('is-visible');
      stickyCart.setAttribute('aria-hidden', 'false');
      /* Tell the sticky-ATC's state machine that cart wants it hidden. */
      if (stickyBar && typeof stickyBar.__setCartWantsHidden === 'function') {
        stickyBar.__setCartWantsHidden(true);
      }
    }

    function hideCart() {
      stickyCart.classList.remove('is-visible');
      stickyCart.setAttribute('aria-hidden', 'true');
      setTimeout(function () { stickyCart.hidden = true; }, 240);
      /* Release the cart-side hold; sticky-ATC's scroll observers govern visibility. */
      if (stickyBar && typeof stickyBar.__setCartWantsHidden === 'function') {
        stickyBar.__setCartWantsHidden(false);
      }
    }

    function applyCartState(cart) {
      if (!cart) return;
      var count = cart.item_count || 0;
      if (count > 0) {
        if (countEl)   countEl.textContent   = String(count);
        if (summaryEl) summaryEl.textContent = count + (count === 1 ? ' item added' : ' items added');
        if (totalEl)   totalEl.textContent   = formatMoney(cart.total_price || 0);
        showCart();
      } else {
        hideCart();
      }
    }

    /* Always re-fetch /cart.js — pubsub payloads carry the line item, not the
       full cart (cart_count / total_price). One source of truth = /cart.js. */
    function fetchCart() {
      fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart.js', {
        headers: { 'Accept': 'application/json' }
      })
        .then(function (r) { return r.json(); })
        .then(applyCartState)
        .catch(function () { /* network error: leave whatever state we have */ });
    }

    /* Initial cart fetch on page load */
    fetchCart();

    /* Subscribe to Taste theme's cart update pubsub event — always re-fetch */
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () { fetchCart(); });
    }

    /* Tap → open cart drawer */
    if (trigger) {
      trigger.addEventListener('click', function () {
        var drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.open === 'function') {
          drawer.open();
          return;
        }
        /* Fallback: click the header cart icon (Taste's cart-icon-bubble) */
        var bubble = document.getElementById('cart-icon-bubble') || document.querySelector('a[href$="/cart"]');
        if (bubble && typeof bubble.click === 'function') {
          bubble.click();
        } else {
          window.location = (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart';
        }
      });
    }

    /* ---------- NEW: HIDE WHEN CART DRAWER IS OPEN ----------
       Watch <cart-drawer>'s .active class. When the drawer opens we add
       .is-drawer-open to the sticky bar so its CSS rule hides it; when
       the drawer closes the class is removed and the bar reappears. */
    var drawerEl = document.querySelector('cart-drawer');
    if (drawerEl && typeof MutationObserver === 'function') {
      var syncDrawerState = function () {
        if (drawerEl.classList.contains('active')) {
          stickyCart.classList.add('is-drawer-open');
        } else {
          stickyCart.classList.remove('is-drawer-open');
        }
      };
      syncDrawerState();
      new MutationObserver(syncDrawerState).observe(drawerEl, {
        attributes: true,
        attributeFilter: ['class']
      });
    }
  }

  /* ==========================================================================
     Initialise All
     ========================================================================== */
  /* Fix 10 — Mirror aria-hidden onto `inert` so focusable descendants
     inside hidden modals/drawers (search modal, cart drawer when closed)
     stop being tab-reachable. Resolves the Lighthouse a11y check
     "[aria-hidden='true'] elements contain focusable descendants" without
     touching the underlying Taste web components. */
  function initAriaHiddenInertShim() {
    if (!('MutationObserver' in window)) return;

    function applyInert(el) {
      var hidden = el.getAttribute('aria-hidden') === 'true';
      if (hidden) {
        if (!el.hasAttribute('inert')) el.setAttribute('inert', '');
      } else {
        if (el.hasAttribute('inert')) el.removeAttribute('inert');
      }
    }

    /* Initial pass over any element already carrying aria-hidden */
    document.querySelectorAll('[aria-hidden="true"]').forEach(applyInert);

    /* Watch for future attribute changes on aria-hidden, plus newly-added
       elements that arrive with aria-hidden already set. */
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.type === 'attributes' && m.attributeName === 'aria-hidden') {
          applyInert(m.target);
        } else if (m.type === 'childList') {
          m.addedNodes.forEach(function (node) {
            if (node.nodeType !== 1) return;
            if (node.hasAttribute && node.hasAttribute('aria-hidden')) applyInert(node);
            if (node.querySelectorAll) {
              node.querySelectorAll('[aria-hidden="true"]').forEach(applyInert);
            }
          });
        }
      });
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['aria-hidden'],
      subtree: true,
      childList: true
    });
  }

  function init() {
    initScrollReveal();
    initScrollRevealLegacy();
    initSmoothScroll();
    initFragmentPreselect();
    initStickyATC();
    initStickyCart();
    initFAQ();
    initHeaderBottomPosition();
    initAriaHiddenInertShim();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();